import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TennisBall, Shuffle } from "@phosphor-icons/react";

function teamName(t) { return t.p2 ? `${t.p1} / ${t.p2}` : t.p1; }

// Overlay shown at the start of a live match to pick which team serves first.
// Supports manual selection or an animated random toss.
export default function ServeTossDialog({ team1, team2, onSelect }) {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);

  const teams = [
    { n: 1, name: teamName(team1), color: "#00D1FF", accent: "blue" },
    { n: 2, name: teamName(team2), color: "#CCFF00", accent: "green" },
  ];

  const toss = () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    const winner = Math.random() < 0.5 ? 1 : 2;
    setTimeout(() => {
      setSpinning(false);
      setResult(winner);
    }, 1800);
  };

  return (
    <motion.div
      data-testid="serve-toss-dialog"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[70] bg-[#0A0A0C]/95 backdrop-blur-md grain flex flex-col items-center justify-center px-6"
    >
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-center mb-2"
      >
        ¿Quién saca primero?
      </motion.p>
      <p className="text-muted-foreground text-sm tracking-widest uppercase mb-10">Elige el equipo o haz un sorteo</p>

      {/* Animated ball during toss */}
      <div className="h-24 flex items-center justify-center mb-8">
        <AnimatePresence mode="wait">
          {spinning && (
            <motion.div
              key="spin"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ rotate: 360, scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ rotate: { repeat: Infinity, duration: 0.5, ease: "linear" }, default: { duration: 0.3 } }}
            >
              <TennisBall size={80} weight="fill" className="text-padel-green" />
            </motion.div>
          )}
          {result && (
            <motion.div
              key="result"
              data-testid="toss-result"
              initial={{ scale: 0.3, opacity: 0 }}
              animate={{ scale: [1.4, 1], opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <TennisBall size={56} weight="fill" className="mx-auto mb-1" style={{ color: result === 1 ? "#00D1FF" : "#CCFF00" }} />
              <p className="font-heading text-xl font-extrabold uppercase" style={{ color: result === 1 ? "#00D1FF" : "#CCFF00" }}>
                Saca {teams[result - 1].name}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="grid grid-cols-2 gap-4 w-full max-w-2xl">
        {teams.map((t) => (
          <motion.button
            key={t.n}
            data-testid={`toss-team${t.n}-btn`}
            disabled={spinning || !!result}
            onClick={() => onSelect(t.n)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex flex-col items-center justify-center gap-3 p-6 border bg-[#131417] rounded-none disabled:opacity-40 transition-colors"
            style={{ borderColor: `${t.color}66` }}
          >
            <TennisBall size={32} weight="fill" style={{ color: t.color }} />
            <span className="font-heading text-lg sm:text-xl font-extrabold uppercase leading-tight text-center" style={{ color: t.color }}>
              {t.name}
            </span>
          </motion.button>
        ))}
      </div>

      <motion.button
        data-testid="toss-random-btn"
        disabled={spinning || !!result}
        onClick={toss}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className="mt-8 flex items-center gap-2 px-8 h-12 bg-white/5 border border-white/20 text-white font-heading uppercase tracking-widest rounded-none hover:bg-white/10 disabled:opacity-40 transition-colors"
      >
        <Shuffle size={20} weight="bold" /> Sortear al azar
      </motion.button>

      {result && (
        <motion.button
          data-testid="toss-start-btn"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3 }}
          onClick={() => onSelect(result)}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          className="mt-4 px-10 h-14 bg-padel-green text-black font-heading text-lg uppercase tracking-widest rounded-none hover:brightness-110 transition"
        >
          Empezar
        </motion.button>
      )}
    </motion.div>
  );
}
